import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { Text } from '@app/components/core/typography/Text'
import { Initiative } from '@schema/initiative'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useMemo } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { EditContainer } from './blocks/shared/EditContainer'
import { useEditContainerClose } from './blocks/shared/useEditContainerClose'
import { FieldEditActions } from './blocks/shared/FieldEditActions'
import { useFormContext } from 'react-hook-form'
import { enrich } from '@i18n/localizeHelper'
import {
  createContactDetailsSchema,
  type ContactDetailsFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import {
  contactDetailsDefaultValues,
  contactDetailsToEditPayload,
} from '@app/pages/dashboard/initiative/shared/forms/contactDetails/contactDetailsFormMappers'
import {
  ContactDetailsFormFields,
  ContactDetailsFormInitialSync,
} from '@app/pages/dashboard/initiative/shared/forms/contactDetails/ContactDetailsFormFields'
import { InitiativeWithId } from '../../../shared/initiativeTypes'

export type ContactDetailsEditProps = {
  initiative: InitiativeWithId
}

const ContactDetailsAboutEditFormBody = ({
  initial,
}: {
  initial: ContactDetailsFormValues
}) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error(
      'ContactDetailsAboutEditFormBody requires an EditContainer ancestor',
    )
  }

  const { reset, formState } = useFormContext<ContactDetailsFormValues>()

  return (
    <Column alignItems="stretch">
      <ContactDetailsFormInitialSync initial={initial} />
      <ContactDetailsFormFields />
      <FieldEditActions
        onCancel={() => {
          reset(initial)
          close()
        }}
        saveDisabled={!formState.isValid || formState.isSubmitting}
      />
    </Column>
  )
}

const ContactDetailsView = ({
  initiative,
}: {
  initiative: Partial<Initiative>
}) => {
  const name = [initiative.contactFirstName, initiative.contactLastName]
    .filter((s) => (s ?? '').trim() !== '')
    .join(' ')

  return (
    <Column gap="0.5rem" alignItems="stretch">
      <Text.Medium color="var(--palette-blue-500)">
        {name !== '' ? name : '—'}
      </Text.Medium>
      <Text.Medium color="var(--palette-blue-500)">
        {(initiative.contactEmail ?? '').trim() !== ''
          ? initiative.contactEmail
          : '—'}
      </Text.Medium>
      <Text.Medium color="var(--palette-blue-500)">
        {(initiative.contactPhoneNumber ?? '').trim() !== ''
          ? initiative.contactPhoneNumber
          : '—'}
      </Text.Medium>
    </Column>
  )
}

const ContactDetailsFormInner = ({
  initiative,
}: {
  initiative: Partial<Initiative>
}) => {
  const translationsRoot = useTranslations().app
  const formValidation = translationsRoot.form.validation
  const common = translationsRoot.common
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const close = useEditContainerClose()
  if (!close) {
    throw new Error(
      'ContactDetailsFormInner requires an EditContainer ancestor',
    )
  }

  const {
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhoneNumber,
    id: initiativeId,
  } = initiative

  const schema = useMemo(
    () =>
      createContactDetailsSchema({
        required: formValidation.required,
        invalidEmail: formValidation.invalidEmail,
        phoneMinLength: enrich(formValidation.minLength, 9),
      }),
    [formValidation],
  )

  const initialValues = useMemo(
    () =>
      contactDetailsDefaultValues({
        contactFirstName,
        contactLastName,
        contactEmail,
        contactPhoneNumber,
      }),
    [contactFirstName, contactLastName, contactEmail, contactPhoneNumber],
  )

  const onSubmit = useCallback(
    (data: ContactDetailsFormValues) => {
      if (!initiativeId) {
        return
      }
      void client
        .call('edit-initiative', {
          id: initiativeId,
          value: contactDetailsToEditPayload(data),
        })
        .then((success) => {
          if (success) {
            showSuccessToast(common.toastSuccess)
            close()
          } else {
            showFailToast()
          }
        })
        .catch(() => showFailToast())
    },
    [
      client,
      initiativeId,
      close,
      showFailToast,
      showSuccessToast,
      common.toastSuccess,
    ],
  )

  return (
    <Form<ContactDetailsFormValues>
      schema={schema}
      defaultValues={initialValues}
      mode="onChange"
      onSubmit={onSubmit}
    >
      {() => <ContactDetailsAboutEditFormBody initial={initialValues} />}
    </Form>
  )
}

export const ContactDetailsEdit = ({ initiative }: ContactDetailsEditProps) => {
  const view = useTranslations().app.dashboard.initiative.view

  return (
    <EditContainer title={view.contactDetailsSection}>
      <EditContainer.View>
        <ContactDetailsView initiative={initiative} />
      </EditContainer.View>
      <EditContainer.Edit>
        <ContactDetailsFormInner initiative={initiative} />
      </EditContainer.Edit>
    </EditContainer>
  )
}
